/**
 * Notification Center & Milestone Tracker
 * Generates in-app notifications for financial milestones and achievements
 * Uses localStorage for persistence in demo mode
 */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Bell, Check, X } from 'lucide-react';

const STORAGE_KEY = 'wt_notifications';
const DISMISSED_KEY = `${STORAGE_KEY}_dismissed`;
const READ_KEY = `${STORAGE_KEY}_read`;
const LAST_NET_WORTH_KEY = `${STORAGE_KEY}_last_net_worth`;

const loadJsonArray = (key) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const loadNotifications = () => loadJsonArray(STORAGE_KEY);
const loadDismissedIds = () => new Set(loadJsonArray(DISMISSED_KEY));
const loadReadIds = () => new Set(loadJsonArray(READ_KEY));
const saveIdSet = (key, ids) => localStorage.setItem(key, JSON.stringify([...ids]));
const notifyChange = () => window.dispatchEvent(new CustomEvent('wt_notifications_changed'));
const saveNotifications = (n) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(n));
  notifyChange();
};

const rememberRead = (ids) => {
  const readIds = loadReadIds();
  ids.forEach((id) => readIds.add(id));
  saveIdSet(READ_KEY, readIds);
};

const rememberDismissed = (id) => {
  const dismissedIds = loadDismissedIds();
  dismissedIds.add(id);
  saveIdSet(DISMISSED_KEY, dismissedIds);
};

const MILESTONE_AMOUNTS = [1000, 5000, 10000, 25000, 50000, 100000, 250000, 500000, 1000000];

const buildTrendId = (type, prevNetWorth, netWorth) => (
  `${type}_${Math.round(prevNetWorth)}_${Math.round(netWorth)}`
);

const checkMilestones = (accounts = { assets: [], liabilities: [] }, prevNetWorth = 0, formatCurrencyFn = null) => {
  const formatAmount = formatCurrencyFn || ((value) => `$${value.toLocaleString()}`);
  const assets = (accounts.assets || []).reduce((s, a) => s + (Number(a.currentValue) || 0), 0);
  const liabilities = (accounts.liabilities || []).reduce((s, a) => s + (Number(a.currentValue) || 0), 0);
  const netWorth = assets - liabilities;
  const notifications = [];

  // Net worth milestones
  MILESTONE_AMOUNTS.forEach(milestone => {
    if (netWorth >= milestone && prevNetWorth < milestone) {
      notifications.push({
        id: `milestone_nw_${milestone}`,
        type: 'milestone',
        icon: '🏆',
        title: `Net Worth Milestone!`,
        message: `You've crossed ${formatAmount(milestone)} in net worth!`,
        timestamp: new Date().toISOString(),
        read: false
      });
    }
  });

  // Positive trend
  if (prevNetWorth > 0 && netWorth > prevNetWorth * 1.05) {
    notifications.push({
      id: buildTrendId('growth', prevNetWorth, netWorth),
      type: 'insight',
      icon: '📈',
      title: 'Strong Growth!',
      message: `Your net worth grew by ${((netWorth - prevNetWorth) / prevNetWorth * 100).toFixed(1)}% since last check.`,
      timestamp: new Date().toISOString(),
      read: false
    });
  }

  // Negative trend warning
  if (prevNetWorth > 0 && netWorth < prevNetWorth * 0.95) {
    notifications.push({
      id: buildTrendId('decline', prevNetWorth, netWorth),
      type: 'warning',
      icon: '⚠️',
      title: 'Net Worth Decline',
      message: `Your net worth decreased by ${((prevNetWorth - netWorth) / prevNetWorth * 100).toFixed(1)}%. Review your recent changes.`,
      timestamp: new Date().toISOString(),
      read: false
    });
  }

  return notifications;
};

const NotificationCenter = ({ accounts = { assets: [], liabilities: [] }, renderButton = true, className = '', formatCurrency = null }) => {
  const [notifications, setNotifications] = useState(loadNotifications);
  const [isOpen, setIsOpen] = useState(false);

  // Context formatters are recreated each render; keep the latest in a ref so the
  // milestone effect doesn't have to re-run on every render.
  const formatCurrencyRef = useRef(formatCurrency);
  formatCurrencyRef.current = formatCurrency;

  useEffect(() => {
    const refresh = () => setNotifications(loadNotifications());
    window.addEventListener('storage', refresh);
    window.addEventListener('wt_notifications_changed', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('wt_notifications_changed', refresh);
    };
  }, []);

  const accountCount = (accounts.assets || []).length + (accounts.liabilities || []).length;

  const currentNetWorth = useMemo(() => {
    const assets = (accounts.assets || []).reduce((s, a) => s + (Number(a.currentValue) || 0), 0);
    const liabilities = (accounts.liabilities || []).reduce((s, a) => s + (Number(a.currentValue) || 0), 0);
    return assets - liabilities;
  }, [accounts]);

  useEffect(() => {
    // Header-only instances do not receive account data. Do not let them reset
    // the last net worth baseline; otherwise hidden dashboard generators can
    // recreate dismissed/read notifications after theme changes or login cycles.
    if (accountCount === 0) return;

    const lastNetWorth = Number(localStorage.getItem(LAST_NET_WORTH_KEY)) || 0;
    const generated = checkMilestones(accounts, lastNetWorth, formatCurrencyRef.current);

    if (generated.length > 0) {
      setNotifications(prev => {
        const existingIds = new Set(prev.map(n => n.id));
        const dismissedIds = loadDismissedIds();
        const readIds = loadReadIds();
        const generatedToAdd = generated
          .filter(n => !existingIds.has(n.id) && !dismissedIds.has(n.id))
          .map(n => ({ ...n, read: readIds.has(n.id) || n.read }));

        if (generatedToAdd.length === 0) return prev;

        const merged = [...generatedToAdd, ...prev];
        saveNotifications(merged);
        return merged;
      });
    }

    localStorage.setItem(LAST_NET_WORTH_KEY, String(currentNetWorth));
  }, [accounts, accountCount, currentNetWorth]);

  const unreadCount = useMemo(() => notifications.filter(n => !n.read).length, [notifications]);

  const handleMarkRead = (id) => {
    const updated = notifications.map(n => n.id === id ? { ...n, read: true } : n);
    rememberRead([id]);
    setNotifications(updated);
    saveNotifications(updated);
  };

  const handleMarkAllRead = () => {
    const updated = notifications.map(n => ({ ...n, read: true }));
    rememberRead(updated.map((n) => n.id));
    setNotifications(updated);
    saveNotifications(updated);
  };

  const handleDismiss = (id) => {
    const updated = notifications.filter(n => n.id !== id);
    rememberDismissed(id);
    setNotifications(updated);
    saveNotifications(updated);
  };

  const typeColors = {
    milestone: 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900',
    insight: 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900',
    warning: 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900'
  };

  if (!renderButton) {
    return null;
  }

  return (
    <div data-testid="notification-center" className={`relative ${className}`.trim()}>
      <button
        data-testid="btn-notifications"
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-50 max-h-96 overflow-y-auto">
          <div className="flex items-center justify-between p-3 border-b border-gray-100 dark:border-gray-800">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Notifications</h4>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-blue-500 hover:text-blue-600"
              >
                Mark all read
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p>No notifications yet.</p>
              <p className="text-xs mt-1">Milestones and insights will appear here.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {[...notifications].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).map(n => (
                <div
                  key={n.id}
                  data-testid={`notification-${n.id}`}
                  className={`p-3 border-l-4 ${typeColors[n.type] || typeColors.insight} ${n.read ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-start gap-2">
                    <span className="text-lg flex-shrink-0">{n.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{n.title}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{n.message}</p>
                      <p className="text-[10px] text-gray-400 mt-1">
                        {new Date(n.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {!n.read && (
                        <button onClick={() => handleMarkRead(n.id)} className="text-gray-300 hover:text-blue-500" aria-label={`Mark ${n.title} read`}>
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button onClick={() => handleDismiss(n.id)} className="text-gray-300 hover:text-red-500" aria-label={`Dismiss ${n.title}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export { NotificationCenter, checkMilestones };
export default NotificationCenter;
